import { ref, onMounted, computed, watch } from 'vue';
import { useFavicon } from '@vueuse/core';
import { usePage } from '@inertiajs/vue3';
import axios from 'axios';

const opened = ref(false);
const unreadChangeIndicate = ref(false);
const messages = ref([]);
const total = ref(0);
const page = ref(0);
const isLoadedAll = ref(false);

const loading = ref({
    notifications: false,
});

let initialLoadStarted = false;

const canViewNotifications = () => {
    const permissions = (usePage().props as any)?.me?.permissions;

    // Fail open when the permission key is not shared (e.g. older payloads)
    return permissions?.['notifications.view'] !== false;
};

const loadNotifications = async () => {
    if (!canViewNotifications()) {
        return;
    }

    try {
        loading.value.notifications = true;

        const { data } = await axios.get(route('notifications.index'), {
            params: {
                page: page.value + 1,
            },
        });

        // Ensure data.notifications is always an array
        const notifications = data?.notifications || [];

        if (page.value === 0) {
            messages.value = notifications;
        } else {
            messages.value = [...(messages.value || []), ...notifications];
        }

        total.value = data?.total || 0;

        if (notifications.length === 0) {
            isLoadedAll.value = true;
        }
    } catch (error) {
        console.error('Failed to load notifications:', error);
        // Ensure messages is always an array even if API fails
        if (!Array.isArray(messages.value)) {
            messages.value = [];
        }
    } finally {
        loading.value.notifications = false;
    }
};

const loadMore = async () => {
    page.value = page.value + 1;

    loadNotifications();
};

const unreadMessages = computed(() => {
    // Ensure messages.value is always an array before filtering
    return Array.isArray(messages.value) ? messages.value.filter((item) => item.unread) : [];
});

const unreadCount = computed(() => {
    return unreadMessages.value.length;
});

const hasUnread = computed(() => {
    return unreadCount.value > 0;
});

const hasMessages = computed(() => {
    // Ensure messages.value is always an array before checking length
    return Array.isArray(messages.value) && messages.value.length > 0;
});

// Subscriber for notifications
if (window.Echo) {
    window.Echo.channel('admin').listen('.inquiry.created', () => {
        loadNotifications();
    });

    window.Echo.channel('admin').listen('.order.created', () => {
        loadNotifications();
    });

    window.Echo.channel('admin').listen('.notification', () => {
        loadNotifications();
    });
}

function markUnreadAsRead() {
    const unreadIds = unreadMessages.value.map((item) => item.id);

    if (unreadIds.length === 0) {
        return;
    }

    axios
        .post(route('notifications.mark-as-read'), {
            ids: unreadIds,
        })
        .then(({ data }) => {
            // Replace unread messages with read messages
            if (Array.isArray(messages.value)) {
                messages.value = messages.value.map((item) => {
                    if (unreadIds.includes(item.id)) {
                        item.unread = false;
                    }
                    return item;
                });
            }

            total.value = data?.total || total.value;
        })
        .catch((error) => {
            console.error('Failed to mark notifications as read:', error);
        });
}

function delayedMarkUnreadAsRead() {
    setTimeout(markUnreadAsRead, 1000);
}

watch(
    opened,
    (value) => {
        if (value) {
            delayedMarkUnreadAsRead();
        }
    },
    { immediate: true },
);

export function useNotificationArea() {
    const open = () => {
        opened.value = true;
    };

    const close = () => {
        opened.value = false;
    };

    const toggle = () => {
        opened.value = !opened.value;
    };

    onMounted(() => {
        if (initialLoadStarted) {
            return;
        }
        initialLoadStarted = true;
        loadNotifications();
    });

    /**
     * Favicon — only manage when no custom branding favicon is set
     */
    const page = usePage();
    const hasBrandingFavicon = !!(page.props as any).branding?.favicon;

    if (!hasBrandingFavicon) {
        const faviconType = ref('standard');

        const environment = (page.props as any)?.app?.env || 'local';
        const theme = (page.props as any)?.theme?.theme || 'default';

        const faviconEnvironment = computed(() => (environment === 'production' ? 'production' : 'development'));

        const faviconUrl = computed(() =>
            faviconType.value === 'standard'
                ? `themes/${theme}/images/${faviconEnvironment.value}/favicon-32x32.png`
                : `themes/${theme}/images/${faviconEnvironment.value}/notifications/favicon-32x32.png`,
        );

        useFavicon(faviconUrl, {
            baseUrl: '/',
            rel: 'icon',
        });

        watch(unreadCount, (value) => {
            // Set favicon with notifications or standard according to unread count
            if (value > 0) {
                faviconType.value = 'notifications';
            } else {
                faviconType.value = 'standard';
            }
        });
    }

    watch(unreadCount, () => {
        unreadChangeIndicate.value = true;
        setTimeout(() => {
            unreadChangeIndicate.value = false;
        }, 500);
    });

    return {
        opened,
        open,
        close,
        toggle,
        messages,
        total,
        unreadMessages,
        unreadCount,
        hasUnread,
        hasMessages,
        unreadChangeIndicate,
        markUnreadAsRead,
        delayedMarkUnreadAsRead,
        loadMore,
        loading,
        isLoadedAll,
    };
}
